import { Component, ChangeDetectionStrategy, TemplateRef, effect, inject, input, output } from '@angular/core';
import { CdkDragDrop, CdkDropList, CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { GroupBase } from '../../../models/blueprint.model';
import {
  DataObject,
  RecordUpdateEmission,
  ReportInfo,
  ReportRecord,
  ReportView,
  TemplateProjectionMap,
} from '../../../models/report.model';
import { TemplateField } from '../../../models/template.model';
import { ReportFieldFormatterService } from '../../../report-field-formatter.service';
import { ADDITIONAL_IMAGE_FIELD_ID, IMAGE_FIELD_ID, IMAGE_STATIC } from '../../../shared/templates/config/common';
import { EditableFieldsService } from '../../editable-fields.service';
import { FLOORPLAN_DISPLAY_TEMPLATES } from './record-body.model';
import { onDrop } from '../../presentation-dynamic-view/presentation-report.helper';
import { Router } from '@angular/router';
import { digger } from '@cbre/digger';
import { processCompanyContacts } from '../../../shared/utils/preprocessor-company-contacts-factory';
import { RecordHeaderComponent } from './record-header/record-header.component';
import { NgTemplateOutlet, NgClass, AsyncPipe } from '@angular/common';
import { RecordImageComponent } from './record-image/record-image.component';
import { PresentationDynamicViewImageComponent } from '../../presentation-dynamic-view/record-body/record-image/record-image.component';
import { MatIcon } from '@angular/material/icon';
import { L10nTranslationModule } from 'angular-l10n';
import { FloorPlanComponent } from './floor-plan/floor-plan.component';

import { ReportFieldFormatterPipe } from '../../../shared/pipes/report-field-formatter.pipe';
import { ReportLabelFormatterPipe } from '../../../shared/pipes/report-label-formatter.pipe';
import { IsFieldVisiblePipe } from '../../../shared/pipes/field-visible.pipe';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'ppt-record-body',
  templateUrl: './record-body.component.html',
  styleUrl: './record-body.component.scss',
  imports: [
    RecordHeaderComponent,
    RecordImageComponent,
    PresentationDynamicViewImageComponent,
    NgTemplateOutlet,
    NgClass,
    CdkDropList,
    CdkDrag,
    MatIcon,
    CdkDragHandle,
    L10nTranslationModule,
    FloorPlanComponent,
    AsyncPipe,
    ReportFieldFormatterPipe,
    ReportLabelFormatterPipe,
    IsFieldVisiblePipe
],
})
export class RecordBodyComponent {
  public editableFieldsService = inject(EditableFieldsService);
  private readonly reportFieldFormatterService = inject(ReportFieldFormatterService);
  private readonly router = inject(Router);

  readonly recordNumber = input.required<number>();
  readonly reportData = input.required<ReportInfo>();
  readonly currentView = input.required<ReportView>();
  readonly headerGroups = input.required<GroupBase<TemplateField>[]>();
  readonly groupsToDisplay = input.required<GroupBase<TemplateField>[]>();
  readonly customConfigs = input<{ symbols: DataObject }>();
  readonly confidentialityTmpl = input<TemplateRef<any>>();
  readonly reportLocale = input.required<number>();
  readonly imageGroups = input.required<GroupBase<TemplateField>[]>();
  readonly templateProjectionMap = input.required<TemplateProjectionMap>();
  readonly reportUpdateClick = output<RecordUpdateEmission>();
  readonly removeRecord = output<ReportRecord>();
  readonly reportReordered = output<ReportRecord[]>();

  reportRecordData: ReportRecord;
  allFields: TemplateField[];
  headerFields: TemplateField[];
  tableFields: GroupBase<TemplateField>;
  bodyAttributes: GroupBase<TemplateField>;
  allDataFields: TemplateField[];
  floorPlanTemplateIds = false;
  tableData: any[];
  isSecondaryImageRequired = false;

  constructor() {
    effect(() => this.syncFromInputs());
  }

  private updateAllFieldsFromGroupsToDisplay(groupsToDisplay: GroupBase<TemplateField>[], currentView: ReportView): void {
    if (this.isSecondaryImageRequired && !!groupsToDisplay) {
      const [bodyAttributes, tableFields] = groupsToDisplay;
      this.tableFields = tableFields;
      const fields = bodyAttributes.fields.filter(field => {
        return this.reportFieldFormatterService.isFieldVisible(this.reportRecordData, {
          templateId: currentView.templateId,
          field,
        });
      });
      this.bodyAttributes = { ...bodyAttributes, fields };
      this.allFields = bodyAttributes ? [].concat(...[bodyAttributes].map(group => group.fields)) : [];
    } else {
      this.allFields = [].concat(...(groupsToDisplay || []).map(group => group.fields));
    }
  }

  private syncFromInputs(): void {
    const recordNumber = this.recordNumber();
    const reportData = this.reportData();
    const currentView = this.currentView();
    const groupsToDisplay = this.groupsToDisplay();
    const headerGroups = this.headerGroups();

    this.editableFieldsService.mapEditableFields(groupsToDisplay);

    this.reportRecordData =
      reportData && recordNumber ? reportData.records[recordNumber - 1] : null;

    // To remove duplicate company id records from property
    this.reportRecordData = {
      ...this.reportRecordData,
      data: processCompanyContacts(reportData.entity, this.reportRecordData.data as DataObject),
    };

    this.isSecondaryImageRequired = [43, 44].includes(currentView.templateId);
    this.updateAllFieldsFromGroupsToDisplay(groupsToDisplay, currentView);
    this.allDataFields = (this.allFields || []).filter(
      field => field.id !== IMAGE_FIELD_ID && field.id !== ADDITIONAL_IMAGE_FIELD_ID && field.id !== IMAGE_STATIC
    );
    this.headerFields = []
      .concat(...(headerGroups || []).map(group => group.fields))
      .sort((a, b) => a.options.headerSortOrder - b.options.headerSortOrder);
    this.floorPlanTemplateIds =
      reportData.entity === 'availability' && FLOORPLAN_DISPLAY_TEMPLATES.has(currentView.templateId);
    if (!!reportData && !!reportData.records && this.tableFields && this.tableFields.tableConfigs) {
      const data = digger(this.reportRecordData, this.tableFields.tableConfigs.dataKey);
      this.tableData = Array.isArray(data) ? data : null;
    }
  }

  emitEditableFields(event: MouseEvent, id: number) {
    const emittedObject = this.editableFieldsService.generateRecordUpdateEmission(
      event,
      id,
      this.reportRecordData.data
    );
    if (emittedObject != null) {
      emittedObject.locale = this.reportLocale();
      this.reportUpdateClick.emit(emittedObject);
    }
  }

  goToRecordDetails(recordData: DataObject) {
    // TODO (Ramakrishna): need to implement pipe as like in condensed view for link navigation
    const { entity } = this.reportData();
    const currentRecordId = recordData.entity.id;
    const url = `/records/${entity}/${currentRecordId}/details`;
    window.open(url, '_blank');
  }

  public onReorder(cdkDragDropEvent: CdkDragDrop<Array<{ [key: string]: any }>>, recordData) {
    onDrop(cdkDragDropEvent, recordData, this.reportData(), this.reportReordered, this.recordNumber());
  }
}
